import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import { DesktopDatePicker, DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isoDateOnlyGen } from '#component/project/form/helper';
import { getDateTimeFormat } from '#helper';

const RHFDateTimeField = ({
  name,
  control,
  minDate,
  maxDate,
  disabled,
  dateonly,
  forceError,
  label,
  fullWidth,
  variant,
  className,
  info,
  rules,
  sx
}) => {
  const measurementSystem = useSelector(s => s.account.measurementSystem);
  const format = useMemo(() => getDateTimeFormat(measurementSystem, dateonly ? 'dateOnly' : 'dateTimeShortDateFns'), [measurementSystem, dateonly]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, onBlur, value }, fieldState: { error, isTouched } }) => {
        const handleChange = date => {
          if (Date.parse(date)) {
            if (dateonly) onChange(isoDateOnlyGen(date));
            else onChange(date.toISOString());
          } else if (date === null) {
            onChange(null);
          } else {
            onChange(new Date(''));
          }
        };

        const handleBlur = () => {
          if (dateonly) onBlur(Date.parse(value) ? value : null);
          else onBlur(Date.parse(value) ? new Date(value).toISOString() : null);
        };

        const dateProps = {
          label,
          value: value ? new Date(value) : null,
          minDate: minDate ? new Date(minDate) : undefined,
          maxDate: maxDate ? new Date(maxDate) : undefined,
          onChange: handleChange,
          disabled,
          format,
          className,
          slotProps: {
            textField: {
              fullWidth,
              variant,
              helperText: ((isTouched || forceError) || !!error) && error?.message ?
                error.message :
                info,
              error: ((isTouched || forceError) && !!error) || !!error,
              onBlur: handleBlur,
              sx
            }
          }
        };

        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {dateonly ? (
              <DesktopDatePicker {...dateProps} />
            ) : (
              <DesktopDateTimePicker {...dateProps} />
            )}
          </LocalizationProvider>
        );
      }}
    />
  );
};

export default RHFDateTimeField;
